import { useTranslation } from "react-i18next";
import { MessageCircle, Calendar, Mail, ArrowRight } from "lucide-react";

type CTAType = "whatsapp" | "calendly" | "email";

interface BlogCTAProps {
  type: CTAType;
  heading?: string;
  body?: string;
}

const META: Record<CTAType, { href: string; icon: typeof MessageCircle; defaultLabel: string }> = {
  whatsapp: { href: "https://wa.me/972546497186", icon: MessageCircle, defaultLabel: "Message us on WhatsApp" },
  calendly: { href: "https://calendly.com/pinch-dyna-agency/30min", icon: Calendar, defaultLabel: "Book a free 30-minute call" },
  email: { href: "mailto:hello@dyna-agency.com", icon: Mail, defaultLabel: "Email us at hello@dyna-agency.com" },
};

export function BlogCTA({ type, heading, body }: BlogCTAProps) {
  const { t } = useTranslation();
  const meta = META[type];
  const Icon = meta.icon;
  const isExternal = !meta.href.startsWith("mailto:");

  return (
    <section
      style={{
        marginTop: "var(--s-lg)",
        padding: 32,
        borderRadius: 20,
        background: "linear-gradient(135deg, rgba(90,70,102,0.4), rgba(156,139,194,0.15))",
        border: "1px solid var(--lavender-400)",
        display: "flex",
        flexDirection: "column",
        gap: 14,
        alignItems: "flex-start",
      }}
    >
      <h3
        style={{
          fontFamily: "\"Coolvetica\", system-ui, sans-serif",
          fontSize: "clamp(22px, 2.6vw, 30px)",
          textTransform: "uppercase",
          letterSpacing: "0.02em",
          color: "var(--dyna-fg)",
          margin: 0,
        }}
      >
        {heading || t("pages.blog.cta.defaultHeading")}
      </h3>
      <p
        style={{
          fontFamily: "Geist, system-ui, sans-serif",
          fontSize: 16,
          lineHeight: 1.6,
          color: "var(--dyna-fg-muted)",
          margin: 0,
        }}
      >
        {body || t("pages.blog.cta.defaultBody")}
      </p>
      <a
        href={meta.href}
        {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
        className="dyna-btn dyna-btn--primary"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          textDecoration: "none",
        }}
      >
        <Icon size={16} />
        {meta.defaultLabel}
        <ArrowRight size={16} />
      </a>
    </section>
  );
}
